"use client";

import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  useTheme,
  alpha,
  IconButton,
  Fade,
  Slide,
  Tooltip,
} from "@mui/material";
import {
  Shuffle,
  Bolt,
  TrendingUp,
  AutoAwesome,
  Close,
  Lightbulb,
  // ExpandLess,
  ExpandMore,
} from "@mui/icons-material";

const ProjectIdeaGenerator = () => {
  const theme = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [currentIdea, setCurrentIdea] = useState("");
  const [industry, setIndustry] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [isTinyMode, setIsTinyMode] = useState(true); // Start in tiny mode

  const industries = [
    "E-commerce",
    "SaaS",
    "Healthcare",
    "Finance",
    "Education",
    "Real Estate",
    "Manufacturing",
    "Retail",
    "Hospitality",
    "Logistics",
  ];

  const projectTemplates = [
    "AI-powered {industry} analytics dashboard that predicts customer behavior",
    "Automated {industry} workflow system that saves 10+ hours weekly",
    "Smart {industry} inventory management with real-time tracking",
    "Mobile-first {industry} customer engagement platform",
    "AI chatbot for {industry} customer support and lead qualification",
    "Data visualization tool for {industry} performance metrics",
    "Automated {industry} reporting system with custom alerts",
    "Cloud-based {industry} collaboration platform for teams",
    "AI-driven {industry} marketing automation suite",
    "Real-time {industry} monitoring and alert system",
  ];

  const benefits = [
    "Boost Efficiency",
    "Reduce Costs",
    "Scale Faster",
    "Improve Accuracy",
    "Enhance Customer Experience",
    "Increase Revenue",
    "Automate Tasks",
    "Gain Insights",
  ];

  const generateIdea = (selectedIndustry?: string) => {
    const randomIndustry =
      selectedIndustry ||
      industries[Math.floor(Math.random() * industries.length)];
    const randomTemplate =
      projectTemplates[Math.floor(Math.random() * projectTemplates.length)];

    setIndustry(randomIndustry);
    setCurrentIdea(
      randomTemplate.replace("{industry}", randomIndustry.toLowerCase())
    );
    if (isTinyMode) {
      setIsExpanded(true);
    } else {
      setIsVisible(true);
    }
  };

  const handleGetStarted = () => {
    window.open("/contact", "_blank");
  };

  const toggleSize = () => {
    if (isTinyMode) {
      // Switching from tiny to full
      setIsTinyMode(false);
      setIsExpanded(false);
      setIsVisible(true);
    } else {
      // Switching from full to tiny
      setIsTinyMode(true);
      setIsVisible(false);
      setIsExpanded(false);
    }
  };

  // Tiny Mode - Super Compact
  if (isTinyMode && !isExpanded) {
    return (
      <Fade in={true} timeout={1000}>
        <Tooltip title="Get project inspiration" arrow placement="right">
          <Paper
            component={"button"}
            type="button"
            elevation={8}
            sx={{
              position: "fixed",
              bottom: 55,
              left: 10,
              width: 60,
              height: 60,
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.secondary.main, 0.9)} 100%)`,
              color: "white",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 999,
              cursor: "pointer",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.1)",
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
              },
            }}
            onClick={() => generateIdea()}
          >
            <AutoAwesome sx={{ fontSize: 24 }} />
          </Paper>
        </Tooltip>
      </Fade>
    );
  }

  // Tiny Mode - Expanded (but still compact)
  if (isTinyMode && isExpanded) {
    return (
      <Slide in={isExpanded} direction="up" timeout={300}>
        <Paper
          elevation={12}
          sx={{
            position: "fixed",
            bottom: 24,
            left: 24,
            width: 280,
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            borderRadius: 2,
            overflow: "hidden",
            zIndex: 1000,
            backdropFilter: "blur(10px)",
          }}
        >
          {/* Compact Header */}
          <Box
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              color: "white",
              p: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Lightbulb sx={{ fontSize: 16 }} />
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, fontSize: "0.8rem" }}
              >
                Idea Spark
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip title="Make smaller" arrow>
                <IconButton
                  size="small"
                  onClick={toggleSize}
                  sx={{ color: "white", p: 0.5 }}
                >
                  <ExpandMore sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              <IconButton
                size="small"
                onClick={() => setIsExpanded(false)}
                sx={{ color: "white", p: 0.5 }}
              >
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Compact Content */}
          <Box sx={{ p: 2 }}>
            {/* Industry & Idea */}
            <Chip
              label={industry}
              size="small"
              sx={{
                mb: 1.5,
                background: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                fontWeight: 500,
                fontSize: "0.7rem",
                height: 20,
              }}
            />

            <Typography
              variant="body2"
              sx={{
                mb: 2,
                fontWeight: 500,
                lineHeight: 1.4,
                fontSize: "0.8rem",
                minHeight: 32,
              }}
            >
              {currentIdea}
            </Typography>

            {/* Compact Actions */}
            <Box
              sx={{ display: "flex", gap: 1, justifyContent: "space-between" }}
            >
              <Button
                variant="contained"
                onClick={handleGetStarted}
                size="small"
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  color: "white",
                  fontSize: "0.7rem",
                  px: 1,
                  minWidth: "auto",
                  flex: 1,
                  mr: 0.5,
                }}
              >
                Build This
              </Button>

              <Tooltip title="New idea" arrow>
                <IconButton
                  size="small"
                  onClick={() => generateIdea()}
                  sx={{
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    color: theme.palette.primary.main,
                  }}
                >
                  <Shuffle sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
      </Slide>
    );
  }

  // Full Mode (Original Size)
  return (
    <Slide in={isVisible} direction="up" timeout={500}>
      <Paper
        elevation={16}
        sx={{
          position: "fixed",
          bottom: 24,
          left: 24,
          width: 360,
          maxWidth: "calc(100vw - 48px)",
          background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.98)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          borderRadius: 3,
          overflow: "hidden",
          zIndex: 1000,
          backdropFilter: "blur(10px)",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            color: "white",
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Lightbulb sx={{ fontSize: 20 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
              Project Idea Generator
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Tooltip title="Make smaller" arrow>
              <IconButton
                size="small"
                onClick={toggleSize}
                sx={{ color: "white" }}
              >
                <ExpandMore sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            <IconButton
              size="small"
              onClick={() => setIsVisible(false)}
              sx={{ color: "white" }}
            >
              <Close sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        {/* Content - Same as before */}
        <Box sx={{ p: 3 }}>
          <Chip
            icon={<TrendingUp sx={{ fontSize: 16 }} />}
            label={industry}
            size="small"
            sx={{
              mb: 3,
              background: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
              fontWeight: 500,
              fontSize: "0.75rem",
            }}
          />

          <Typography
            variant="body1"
            sx={{
              mb: 3,
              fontWeight: 500,
              lineHeight: 1.5,
              fontStyle: "italic",
              color: "text.primary",
            }}
          >
            &quot;{currentIdea}&quot;
          </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "text.secondary",
                mb: 1,
                display: "block",
              }}
            >
              POTENTIAL BENEFITS:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {benefits.slice(0, 4).map((benefit, index) => (
                <Chip
                  key={index}
                  label={benefit}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: "0.7rem",
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    color: theme.palette.primary.main,
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexDirection: "column" }}>
            <Button
              variant="contained"
              onClick={handleGetStarted}
              startIcon={<Bolt />}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                color: "white",
                py: 1,
                borderRadius: 2,
                fontWeight: 600,
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
                },
              }}
            >
              Bring This Idea to Life
            </Button>

            <Button
              variant="outlined"
              onClick={() => generateIdea()}
              startIcon={<Shuffle />}
              sx={{
                borderColor: alpha(theme.palette.primary.main, 0.3),
                color: theme.palette.primary.main,
                py: 0.8,
                borderRadius: 2,
                fontSize: "0.8rem",
              }}
            >
              Generate Another Idea
            </Button>
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "text.secondary",
                mb: 1,
                display: "block",
              }}
            >
              OR PICK AN INDUSTRY:
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
              {industries.slice(0, 5).map((ind) => (
                <Chip
                  key={ind}
                  label={ind}
                  size="small"
                  clickable
                  onClick={() => generateIdea(ind)}
                  sx={{
                    fontSize: "0.65rem",
                    background: alpha(theme.palette.primary.main, 0.05),
                    "&:hover": {
                      background: alpha(theme.palette.primary.main, 0.1),
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
};

export default ProjectIdeaGenerator;
